import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Phone, ArrowRight, Clock, Wrench, ShieldCheck, Smile, BadgeDollarSign, Truck } from "lucide-react";
import { base44 } from "@/api/base44Client";
import { Link } from "react-router-dom";
import { useTheme } from "@/lib/ThemeContext";
import PullToRefresh from "@/components/PullToRefresh";

// Shared Animation Keyframes
const globalStyles = `
  @keyframes floatA { 
    0%, 100% { transform: translateY(0) rotate(0deg); } 
    50% { transform: translateY(-15px) rotate(2deg); } 
  }
  @keyframes shimmer {
    100% { transform: translateX(100%); }
  }
  @keyframes fadeInLeft {
    from { opacity: 0; transform: translateX(-40px); }
    to { opacity: 1; transform: translateX(0); }
  }
  @keyframes fadeInScale {
    from { opacity: 0; transform: scale(0.9) translateY(30px); }
    to { opacity: 1; transform: scale(1) translateY(0); }
  }
  @keyframes twinkle {
    0%, 100% { opacity: 0.2; transform: scale(0.8); }
    50% { opacity: 1; transform: scale(1.2); }
  }
`;

// Deterministic star generator using a seeded pseudo-random for natural spread
function generateStars(count) {
  const stars = [];
  // Use a LCG pseudo-random for stable but natural-looking positions
  let seed = 42;
  const rand = () => { seed = (seed * 1664525 + 1013904223) & 0xffffffff; return (seed >>> 0) / 0xffffffff; };
  for (let i = 0; i < count; i++) {
    stars.push({
      x: rand() * 100,
      y: rand() * 100,
      size: 1.5 + rand() * 3.5,       // 1.5px – 5px
      delay: (rand() * 4).toFixed(2),
      duration: (1.5 + rand() * 3).toFixed(1),
      opacity: 0.5 + rand() * 0.5,
    });
  }
  return stars;
}
const STARS = generateStars(50);

const AnimatedElement = ({ children, className = "", delay = 0 }) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setTimeout(() => setIsVisible(true), delay);
        observer.unobserve(el);
      }
    }, { threshold: 0.1, rootMargin: '0px 0px -50px 0px' });
    
    observer.observe(el);
    return () => observer.disconnect();
  }, [delay]);
  
  return (
    <div 
      ref={ref} 
      className={`transition-all duration-1000 ease-out ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
      } ${className}`}
    >
      {children}
    </div>
  );
};

function HeroSection({ heroContent }) {
  const subtitle = heroContent?.subtitle || "Exciting inflatable water slide rentals for backyard parties, summer celebrations, and events across Houston, Texas!";
  const { dark } = useTheme();

  return (
    <section className="relative min-h-[750px] lg:min-h-[900px] flex items-center pt-24 pb-32 -mt-16">
      {/* Background image */}
      <div className="absolute inset-0 overflow-hidden">
        <img
          src="https://images.weserv.nl/?url=media.base44.com/images/public/6a2225f85d295b30c327a9d5/f7eabfb3b_TopWebsite.jpg&w=1200&output=webp&q=70"
          srcSet="https://images.weserv.nl/?url=media.base44.com/images/public/6a2225f85d295b30c327a9d5/f7eabfb3b_TopWebsite.jpg&w=480&output=webp&q=65 480w, https://images.weserv.nl/?url=media.base44.com/images/public/6a2225f85d295b30c327a9d5/f7eabfb3b_TopWebsite.jpg&w=800&output=webp&q=70 800w, https://images.weserv.nl/?url=media.base44.com/images/public/6a2225f85d295b30c327a9d5/f7eabfb3b_TopWebsite.jpg&w=1200&output=webp&q=70 1200w"
          sizes="100vw"
          alt=""
          fetchpriority="high"
          className="absolute inset-0 w-full h-full object-cover object-center transition-all duration-500"
          style={{ opacity: dark ? 0.35 : 1 }}
        />
        {/* Light mode: soft white wash / Dark mode: deep night overlay */}
        {dark ? (
          <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(5,10,30,0.82) 0%, rgba(10,20,60,0.78) 50%, rgba(5,10,30,0.85) 100%)" }} />
        ) : (
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.35) 100%)" }} />
        )}

        {/* Stars — only in dark mode */}
        {dark && (
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {STARS.map((star, i) => (
              <div
                key={i}
                className="absolute rounded-full bg-white"
                style={{
                  left: `${star.x}%`,
                  top: `${star.y}%`,
                  width: `${star.size}px`,
                  height: `${star.size}px`,
                  opacity: star.opacity,
                  animation: `twinkle ${star.duration}s ${star.delay}s ease-in-out infinite`,
                }}
              />
            ))}
          </div>
        )}

        {/* Bottom Wave */}
        <svg className="absolute bottom-0 left-0 w-full h-4 sm:h-6 text-background z-20" viewBox="0 0 1440 74" fill="currentColor" preserveAspectRatio="none">
          <path d="M0,56.5c0,0,298.666-85.333,720-33.333c421.333,52,720-33.333,720-33.333v84.667H0V56.5z" />
        </svg>
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center">
          
          {/* Left: Text Content */}
          <div
            style={{ animation: "fadeInLeft 0.8s ease-out both" }}
            className="flex flex-col items-center text-center"
          >
            <img
              src="https://images.weserv.nl/?url=media.base44.com/images/public/6a2225f85d295b30c327a9d5/86f6c81c7_waterslideshoustontx_com_Textnew_6a5dd2c4.png&w=480&output=webp&q=70"
              alt="Make a Splash with Houston Water Slide Rentals"
              decoding="async"
              width={576}
              height={262}
              className="w-full max-w-xl object-contain mb-6 drop-shadow-xl"
            />
            <p className="text-lg sm:text-xl font-medium mb-2 max-w-xl text-center" style={{ color: dark ? "rgba(255,255,255,0.9)" : undefined }}>
              {subtitle.split("Houston")[0]}
            </p>
            <p className="font-extrabold text-2xl sm:text-3xl mb-8 tracking-wide text-center w-full" style={{ color: "#ff6b00", fontFamily: "Fredoka, var(--font-heading)" }}>
              Houston, Texas!
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto justify-center">
              <Link 
                to="/browse-rentals" 
                className="group relative overflow-hidden inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground font-bold px-8 py-4 rounded-full shadow-xl hover:scale-105 transition-all duration-300 hover:shadow-primary/40 text-lg"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-background/20 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite]" />
                Check Availability
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <a 
                href="tel:18329704583" 
                className="inline-flex items-center justify-center gap-2 bg-card text-card-foreground font-bold px-8 py-4 rounded-full shadow-lg hover:scale-105 hover:bg-foreground hover:text-background transition-all duration-300 text-lg border border-border/50"
              >
                <Phone className="w-5 h-5" />
                Call 832 970 4583
              </a>
            </div>
          </div>

          {/* Right: Hero Graphic */}
          <div
            style={{ animation: "fadeInScale 0.9s 0.2s ease-out both" }}
            className="flex justify-center lg:justify-end relative"
          >
            <div className="relative w-full max-w-[500px]" style={{ animation: "floatA 6s ease-in-out infinite" }}>
              {/* Glow behind image */}
              <div className="absolute inset-0 bg-primary/20 rounded-full blur-[100px] -z-10 transform scale-90" />
              <img
                src="https://images.weserv.nl/?url=media.base44.com/images/public/6a2225f85d295b30c327a9d5/4ffe23ec3_waterslideshoustontx_com_WaterSlidesHoustonTexas1024x1024_f4716861.png&w=400&output=webp&q=70"
                alt="Water Slides Houston Texas"
                decoding="async"
                width={500}
                height={500}
                className="w-full h-auto object-contain drop-shadow-[0_20px_50px_rgba(0,0,0,0.2)]"
              />
            </div>
          </div>
          
        </div>
      </div>
    </section>
  );
}

const CATEGORIES = [
  {
    label: "Water Slides",
    description: "Exciting inflatable water slides for all ages — perfect for summer parties!",
    href: "/water-slides-rentals",
    image: "https://images.weserv.nl/?url=media.base44.com/images/public/6a2225f85d295b30c327a9d5/1422c2db0_generated_image.png&w=400&output=webp&q=65",
    color: "from-blue-500/10 to-cyan-400/10",
    border: "hover:border-blue-400",
    badge: "🌊",
  },
  {
    label: "Party Rentals",
    description: "Tables and chairs.",
    href: "/party-rentals",
    image: "https://images.weserv.nl/?url=media.base44.com/images/public/6a2225f85d295b30c327a9d5/a87de325f_generated_image.png&w=400&output=webp&q=65",
    color: "from-orange-400/10 to-yellow-300/10",
    border: "hover:border-orange-400",
    badge: "🎉",
  },
  {
    label: "Add-Ons & Extras",
    description: "Generators, lighting, and extras to complete your perfect event setup.",
    href: "/add-ons",
    image: "https://images.weserv.nl/?url=media.base44.com/images/public/6a2225f85d295b30c327a9d5/fe9e7efab_generated_image.png&w=400&output=webp&q=65",
    color: "from-green-400/10 to-emerald-300/10",
    border: "hover:border-green-400",
    badge: "✨",
  },
];

function CategoriesSection() {
  return (
    <section id="slides" className="bg-background py-20 relative">
      <div className="max-w-7xl mx-auto px-6">

        <AnimatedElement>
          <div className="text-center mb-16">
            <h2 className="font-extrabold text-3xl sm:text-4xl md:text-5xl text-foreground tracking-tight mb-4">
              Explore Our Rental Categories
            </h2>
            <div className="w-24 h-1.5 bg-primary rounded-full mx-auto" />
          </div>
        </AnimatedElement>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 mb-16" style={{ alignItems: "stretch" }}>
          {CATEGORIES.map((cat, index) => (
            <AnimatedElement key={cat.label} delay={index * 150} className="flex flex-col h-full">
              <Link
                to={cat.href}
                className={`group flex flex-col items-center cursor-pointer relative bg-gradient-to-b ${cat.color} border border-border/40 ${cat.border} rounded-3xl p-6 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-2 h-full`}
              >
                {/* Image */}
                <div className="relative w-full h-52 rounded-2xl overflow-hidden mb-5">
                  <img
                    src={cat.image}
                    alt={cat.label}
                    loading="lazy"
                    decoding="async"
                    width={550}
                    height={208}
                    className="w-full h-full object-cover group-hover:scale-105 transition-all duration-500 ease-out"
                  />
                </div>

                {/* Label */}
                <div className="text-center">
                  <div className="text-2xl mb-1">{cat.badge}</div>
                  <h3 className="font-extrabold text-foreground text-3xl mb-2 group-hover:text-primary transition-colors">
                    {cat.label}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {cat.description}
                  </p>
                  <span className="mt-4 inline-flex items-center gap-1 text-primary font-semibold text-sm group-hover:gap-2 transition-all">
                    Shop Now <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </Link>
            </AnimatedElement>
          ))}
        </div>

        <AnimatedElement delay={400} className="text-center">
          <Link
            to="/browse-rentals"
            className="group relative overflow-hidden inline-flex items-center justify-center gap-3 bg-primary text-primary-foreground font-bold px-10 py-4 rounded-full shadow-xl hover:scale-105 transition-all duration-300 hover:shadow-primary/40 text-lg"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-background/20 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite]" />
            Browse All Rentals
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Link>
        </AnimatedElement>

      </div>
    </section>
  );
}

function AboutSection({ aboutContent }) {
  const title = aboutContent?.title || "Water Slides Houston Texas";
  const subtitle = aboutContent?.subtitle || "Fun Starts Here";

  return (
    <section className="bg-background py-20 relative overflow-hidden" style={{ contentVisibility: "auto", containIntrinsicSize: "800px" }}>
      {/* Decorative background depth */}
      <div className="absolute top-1/4 right-0 w-96 h-96 bg-secondary/30 rounded-full blur-[100px] pointer-events-none -z-10" />
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-primary/5 rounded-full blur-[80px] pointer-events-none -z-10" />
      
      <AnimatedElement className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="font-extrabold text-foreground mb-3" style={{ fontSize: "3rem" }}>{title}</h2>
          <h3 className="font-black text-2xl text-primary tracking-wide">{subtitle}</h3>
        </div>
        
        <div className="prose prose-lg mx-auto text-foreground/80 leading-relaxed">
          <p className="mb-6">
            Looking for the best <strong className="text-foreground">water slides in Houston Texas</strong>? You're in the right place. We specialize in delivering clean, safe, and exciting inflatable water slides that turn any backyard party into an unforgettable event.
          </p>
          <p className="mb-6">
            Whether you're planning a birthday party, school event, church gathering, corporate celebration, or summer BBQ, our water slide rentals in Houston are designed to keep kids and adults entertained for hours. We offer a wide variety of options to fit every age group and budget.
          </p>
          <p className="mb-8">
            As a locally operated company serving Houston and surrounding areas, we understand what Texas heat feels like — and nothing beats a refreshing inflatable water slide on a hot day. That's why we focus on:
          </p>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
            {[
              { label: "On-time delivery", icon: Clock },
              { label: "Professional setup", icon: Wrench },
              { label: "Sanitized and inspected equipment", icon: ShieldCheck },
              { label: "Friendly customer service", icon: Smile },
              { label: "Affordable pricing with no hidden fees", icon: BadgeDollarSign },
              { label: "Free delivery within 15 miles", icon: Truck },
            ].map(({ label, icon: Icon }, i) => (
              <AnimatedElement key={i} delay={i * 100} className="flex items-center gap-3 bg-card/50 border border-border/50 p-4 rounded-xl shadow-sm hover:shadow-lg hover:-translate-y-1 hover:border-primary/30 transition-all duration-300">
                <div className="flex-shrink-0 w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
                  <Icon className="w-7 h-7 text-primary" />
                </div>
                <span className="font-medium text-foreground">{label}</span>
              </AnimatedElement>
            ))}
          </div>
          
          <p className="mb-6">
            Safety is our top priority. Every unit is thoroughly cleaned and inspected before and after each rental to ensure a safe and worry-free experience for your guests.
          </p>
          <p className="font-medium text-foreground text-center p-6 bg-secondary/20 rounded-2xl border border-secondary/50">
            If you're searching for reliable <strong className="text-primary">water slide rentals in Houston, Texas</strong>, we're here to make your event simple, fun, and stress-free. Browse our inventory and reserve your slide today — dates fill up quickly during summer!
          </p>
        </div>
      </AnimatedElement>
    </section>
  );
}

function WhyChooseSection({ features }) {
  const staticFallback = [
    { id: 1, title: "Clean & Safe", description: "Fully inspected and sanitized before every rental for a worry-free experience.", image_url: "https://images.weserv.nl/?url=media.base44.com/images/public/6a2225f85d295b30c327a9d5/248570e01_waterslideshoustontx_com_OnTimeDelivery_50_1064b63d.png&w=256&output=webp&q=65" },
    { id: 2, title: "On-Time Delivery", description: "Professional setup and on-time delivery every time, guaranteed.", image_url: "https://images.weserv.nl/?url=media.base44.com/images/public/6a2225f85d295b30c327a9d5/3c9aef810_waterslideshoustontx_com_On-Time-Delivery_f0340def.png&w=256&output=webp&q=65" },
    { id: 3, title: "Affordable Pricing", description: "No hidden fees, transparent pricing that fits every budget.", image_url: "https://images.weserv.nl/?url=media.base44.com/images/public/6a2225f85d295b30c327a9d5/fa2ecaa5d_waterslideshoustontx_com_NoHiddenFees_50_051178f3.png&w=256&output=webp&q=65" },
    { id: 4, title: "Local Houston Company", description: "Locally owned and operated — we know Houston heat and love our community!", image_url: "https://images.weserv.nl/?url=media.base44.com/images/public/6a2225f85d295b30c327a9d5/ee5f14355_waterslideshoustontx_com_LocalCompany_50_268dcf5e.png&w=256&output=webp&q=65" },
  ];
  const items = features.length > 0 ? features : staticFallback;

  return (
    <section className="relative pt-20 pb-40 bg-background overflow-hidden" style={{ contentVisibility: "auto", containIntrinsicSize: "1200px" }}>
      
      <AnimatedElement className="relative z-10 max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="font-extrabold text-3xl sm:text-5xl text-foreground tracking-tight mb-4">
            Why Choose Us!
          </h2>
          <div className="w-24 h-1.5 bg-primary rounded-full mx-auto" />
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6 mb-16">
          {items.map((item, index) => (
            <AnimatedElement key={item.id || index} delay={index * 150}>
              <div className="flex flex-col items-center text-center group cursor-default">
                <div className="relative w-52 h-52 sm:w-64 sm:h-64 mb-6">
                  {/* Glowing background behind badge on hover */}
                  <div className="absolute inset-2 rounded-full bg-secondary opacity-0 group-hover:opacity-100 blur-2xl transition-opacity duration-500 -z-10" />
                  <div className="w-full h-full rounded-full overflow-hidden flex items-center justify-center bg-white/0">
                    <img
                      src={item.image_url}
                      alt={item.title}
                      loading="lazy"
                      decoding="async"
                      width={256}
                      height={256}
                      className="w-full h-full object-cover group-hover:scale-110 group-hover:-translate-y-2 transition-transform duration-500 ease-out"
                    />
                  </div>
                </div>
                <h3 className="font-bold text-foreground text-xl mb-2 group-hover:text-primary transition-colors">
                  {item.title}
                </h3>
                <p className="text-foreground/70 text-sm max-w-[250px] leading-relaxed hidden md:block">
                  {item.description}
                </p>
              </div>
            </AnimatedElement>
          ))}
        </div>
        
        <div className="text-center relative z-20">
          <Link to="/browse-rentals" className="group relative overflow-hidden inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground font-bold px-12 py-4 rounded-full shadow-2xl hover:scale-105 transition-all duration-300 hover:shadow-primary/50 text-xl">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-background/20 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite]" />
            Browse Rentals
            <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </AnimatedElement>

      {/* Massive Bottom Ocean Wave to match screenshot */}
      <div className="absolute bottom-0 left-0 w-full pointer-events-none z-0">
        <svg className="w-full h-32 sm:h-64 text-secondary opacity-80" viewBox="0 0 1440 150" fill="currentColor" preserveAspectRatio="none">
           <path d="M0,60 C320,120 420,0 720,60 C1020,120 1120,0 1440,60 L1440,150 L0,150 Z" />
        </svg>
        <svg className="absolute bottom-0 left-0 w-full h-24 sm:h-48 text-secondary" viewBox="0 0 1440 150" fill="currentColor" preserveAspectRatio="none">
           <path d="M0,90 C280,30 480,110 720,90 C960,70 1160,130 1440,90 L1440,150 L0,150 Z" />
        </svg>
      </div>
    </section>
  );
}

export default function Home() {
  const [features, setFeatures] = useState([]);
  const [siteContents, setSiteContents] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    await Promise.all([
      base44.entities.WhyChooseUs.list('sort_order', 20).then(setFeatures).catch(() => {}),
      base44.entities.SiteContent.list('-updated_date', 20).then(setSiteContents).catch(() => {}),
    ]);
  }, []);

  useEffect(() => {
    fetchData().finally(() => setLoading(false));
  }, [fetchData]);

  const heroContent = siteContents.find(c => c.section_key === 'hero');
  const aboutContent = siteContents.find(c => c.section_key === 'about');

  return (
    <PullToRefresh onRefresh={fetchData}>
      <div className={`transition-opacity duration-700 bg-background ${loading ? 'opacity-0' : 'opacity-100'}`}>
        <style>{globalStyles}</style>
        <HeroSection heroContent={heroContent} />
        <CategoriesSection />
        <AboutSection aboutContent={aboutContent} />
        <WhyChooseSection features={features} />
      </div>
    </PullToRefresh>
  );
}